import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../i18n/useAppTranslation';
import { useSiteChoices } from '../../context/SiteChoicesContext';
import type { OptionalPreferenceCategory, SitePreferences } from '../../types/siteChoices';

type ConsentView = 'banner' | 'preferences';

const OPTIONAL_CATEGORY_IDS: OptionalPreferenceCategory[] = [
  'analytics',
  'personalization',
  'marketing',
];

const COOKIE_FALLBACKS = {
  eyebrow: 'Confidentialité',
  title: 'Gérer mes cookies',
  bannerText:
    'Nous utilisons des cookies nécessaires au fonctionnement du site et, avec votre accord, des cookies de mesure, personnalisation et publicité.',
  customize: 'Personnaliser',
  acceptAll: 'Tout accepter',
  rejectAll: 'Tout refuser',
  preferencesCenter: 'Centre de préférences',
  chooseTitle: 'Choisir mes cookies',
  preferencesHint:
    'Les cookies strictement nécessaires restent toujours actifs. Les autres catégories ne seront utilisées que selon votre choix.',
  necessaryStrictTitle: 'Cookies strictement nécessaires',
  necessaryStrictDesc: 'Indispensables au panier, à la sécurité, à la langue et au fonctionnement.',
  requiredLabel: 'Toujours actif',
  save: 'Enregistrer mes choix',
  closeAria: 'Fermer les préférences cookies',
  categories: {
    analytics: {
      title: "Mesure d'audience",
      description: "Aide Skoleom à comprendre l'usage du site, les performances et les erreurs.",
    },
    personalization: {
      title: 'Personnalisation',
      description:
        "Mémorise certains choix d'affichage ou de préférences pour améliorer votre expérience.",
    },
    marketing: {
      title: 'Publicité et réseaux sociaux',
      description:
        'Permet de mesurer les campagnes, activer les pixels publicitaires et proposer des contenus adaptés.',
    },
  },
} as const;

const normalizeFrenchCookieAction = (label: string) =>
  label
    .replace(/\bTout accepte(?:ur)?\b/i, 'Tout accepter')
    .replace(/\bTout refuse\b/i, 'Tout refuser');

export default function PrivacyNoticePanel() {
  const { t, i18n } = useTranslation();
  const { consent, isBannerVisible, acceptAll, rejectAll, savePreferences, closePreferences } =
    useSiteChoices();
  const [isMounted, setIsMounted] = useState(false);
  const [view, setView] = useState<ConsentView>('banner');
  const [preferences, setPreferences] = useState<SitePreferences>(consent.preferences);
  const isFrench = i18n.language?.startsWith('fr');

  const tCookie = useCallback(
    (key: string, fallback: string) => t(key, { defaultValue: fallback }),
    [t],
  );
  const acceptAllLabel = isFrench
    ? normalizeFrenchCookieAction(tCookie('cookies.acceptAll', COOKIE_FALLBACKS.acceptAll))
    : tCookie('cookies.acceptAll', COOKIE_FALLBACKS.acceptAll);
  const rejectAllLabel = isFrench
    ? normalizeFrenchCookieAction(tCookie('cookies.rejectAll', COOKIE_FALLBACKS.rejectAll))
    : tCookie('cookies.rejectAll', COOKIE_FALLBACKS.rejectAll);

  const optionalCategories = useMemo(
    () =>
      OPTIONAL_CATEGORY_IDS.map((id) => ({
        id,
        title: tCookie(`cookies.categories.${id}.title`, COOKIE_FALLBACKS.categories[id].title),
        description: tCookie(
          `cookies.categories.${id}.description`,
          COOKIE_FALLBACKS.categories[id].description,
        ),
      })),
    [tCookie],
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isBannerVisible) return;

    setPreferences(consent.preferences);
    setView(consent.status === 'pending' ? 'banner' : 'preferences');
  }, [consent.preferences, consent.status, isBannerVisible]);

  useEffect(() => {
    if (!isBannerVisible) return undefined;

    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousOverflow;
    };
  }, [isBannerVisible]);

  if (!isMounted || !isBannerVisible) return null;

  const canClose = consent.status !== 'pending';

  const updatePreference = (category: OptionalPreferenceCategory, enabled: boolean) => {
    setPreferences((current) => ({
      ...current,
      necessary: true,
      [category]: enabled,
    }));
  };

  const panel = (
    <div className="privacy-panel" role="dialog" aria-modal="true" aria-labelledby="privacy-title">
      <div className="privacy-panel__panel">
        {canClose ? (
          <button
            type="button"
            className="privacy-panel__close"
            onClick={closePreferences}
            aria-label={tCookie('cookies.closeAria', COOKIE_FALLBACKS.closeAria)}
          >
            x
          </button>
        ) : null}

        {view === 'banner' ? (
          <>
            <p className="privacy-panel__eyebrow">
              {tCookie('cookies.eyebrow', COOKIE_FALLBACKS.eyebrow)}
            </p>
            <h2 id="privacy-title" className="privacy-panel__title">
              {tCookie('cookies.title', COOKIE_FALLBACKS.title)}
            </h2>
            <p className="privacy-panel__text">
              {tCookie('cookies.bannerText', COOKIE_FALLBACKS.bannerText)}
            </p>

            <div className="privacy-panel__actions">
              <button type="button" className="privacy-panel__button secondary" onClick={rejectAll}>
                {rejectAllLabel}
              </button>
              <button
                type="button"
                className="privacy-panel__button ghost"
                onClick={() => setView('preferences')}
              >
                {tCookie('cookies.customize', COOKIE_FALLBACKS.customize)}
              </button>
              <button type="button" className="privacy-panel__button primary" onClick={acceptAll}>
                {acceptAllLabel}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="privacy-panel__eyebrow">
              {tCookie('cookies.preferencesCenter', COOKIE_FALLBACKS.preferencesCenter)}
            </p>
            <h2 id="privacy-title" className="privacy-panel__title">
              {tCookie('cookies.chooseTitle', COOKIE_FALLBACKS.chooseTitle)}
            </h2>
            <p className="privacy-panel__text">
              {tCookie('cookies.preferencesHint', COOKIE_FALLBACKS.preferencesHint)}
            </p>

            <div className="privacy-panel__categories">
              <div className="privacy-panel__category">
                <div>
                  <h3>
                    {tCookie('cookies.necessaryStrictTitle', COOKIE_FALLBACKS.necessaryStrictTitle)}
                  </h3>
                  <p>
                    {tCookie('cookies.necessaryStrictDesc', COOKIE_FALLBACKS.necessaryStrictDesc)}
                  </p>
                </div>
                <span className="privacy-panel__required">
                  {tCookie('cookies.requiredLabel', COOKIE_FALLBACKS.requiredLabel)}
                </span>
              </div>

              {optionalCategories.map((category) => (
                <label className="privacy-panel__category" key={category.id}>
                  <div>
                    <h3>{category.title}</h3>
                    <p>{category.description}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences[category.id]}
                    onChange={(event) => updatePreference(category.id, event.target.checked)}
                  />
                </label>
              ))}
            </div>

            <div className="privacy-panel__actions">
              <button type="button" className="privacy-panel__button secondary" onClick={rejectAll}>
                {rejectAllLabel}
              </button>
              <button
                type="button"
                className="privacy-panel__button ghost"
                onClick={() => savePreferences(preferences)}
              >
                {tCookie('cookies.save', COOKIE_FALLBACKS.save)}
              </button>
              <button type="button" className="privacy-panel__button primary" onClick={acceptAll}>
                {acceptAllLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(panel, document.body);
}
